import { useEffect, useMemo, useState } from 'react';
import type {
  ClientPayment,
  ClientPaymentAllocationInput,
  ExpenseItemWithRelations,
} from '@peles/shared';
import { dateOnlyKey, localDayKey } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useDialog } from '../../context/DialogContext';
import { useExpenseConfig } from '../../hooks/useExpenses';
import { useCreateClientPayment, useUpdateClientPayment } from '../../hooks/useClientPayments';

interface Props {
  open: boolean;
  eventId: string;
  /** Producer-funded expenses on this event, with current clientPaid/clientBalance. */
  payableExpenses: ExpenseItemWithRelations[];
  /** When set, the modal edits this payment. Otherwise it creates a new one. */
  payment?: ClientPayment | null;
  onClose: () => void;
}

export function ClientPaymentModal({ open, eventId, payableExpenses, payment, onClose }: Props) {
  const { data: config } = useExpenseConfig();
  const { confirm } = useDialog();
  const create = useCreateClientPayment();
  const update = useUpdateClientPayment();
  const isEdit = Boolean(payment);

  const [amount, setAmount] = useState<string>('0');
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [paidAt, setPaidAt] = useState<string>(() => localDayKey());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens or payment changes
  useEffect(() => {
    if (!open) return;
    if (payment) {
      setAmount(String(payment.amount));
      setPaymentMethodId(payment.paymentMethodId);
      setPaidAt(dateOnlyKey(payment.paidAt));
      setReference(payment.reference ?? '');
      setNotes(payment.notes ?? '');
      const allocMap: Record<string, string> = {};
      for (const a of payment.allocations) {
        allocMap[a.expenseItemId] = String(a.amount);
      }
      setAllocations(allocMap);
    } else {
      setAmount('0');
      setPaymentMethodId(config?.paymentMethods.find((m) => !m.isProducerFunded)?.id ?? '');
      setPaidAt(localDayKey());
      setReference('');
      setNotes('');
      setAllocations({});
    }
    setError(null);
  }, [open, payment, config]);

  // Default to first non-producer-funded method when config loads
  useEffect(() => {
    if (!paymentMethodId && config?.paymentMethods.length) {
      const first =
        config.paymentMethods.find((m) => !m.isProducerFunded) ?? config.paymentMethods[0];
      setPaymentMethodId(first.id);
    }
  }, [config, paymentMethodId]);

  const numericAmount = Number(amount) || 0;
  const allocationTotal = useMemo(
    () => Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0),
    [allocations],
  );
  const remaining = numericAmount - allocationTotal;

  const groupedByVendor = useMemo(() => {
    const groups = new Map<string, ExpenseItemWithRelations[]>();
    for (const e of payableExpenses) {
      const key = e.vendorName ?? 'Unassigned';
      const list = groups.get(key) ?? [];
      list.push(e);
      groups.set(key, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [payableExpenses]);

  function setAllocationAmount(expenseId: string, value: string) {
    setAllocations((prev) => {
      const next = { ...prev };
      if (!value || value === '0') delete next[expenseId];
      else next[expenseId] = value;
      return next;
    });
  }

  function autoAllocate() {
    let left = numericAmount;
    const next: Record<string, string> = {};
    for (const e of [...payableExpenses].sort((a, b) => {
      // earliest due date first; null-due-date last
      const ad = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const bd = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return ad - bd;
    })) {
      if (left <= 0) break;
      const owed = Math.max(0, e.clientBalance);
      if (owed === 0) continue;
      const apply = Math.min(owed, left);
      next[e.id] = apply.toFixed(2);
      left -= apply;
    }
    setAllocations(next);
  }

  /**
   * Reconciling a producer invoice against a client payment usually means the
   * expense itself is now settled — the client's money *is* its payment. Ask
   * before writing that back, and only when there is something left to close:
   * an expense the producer already paid the vendor for has nothing to record.
   */
  async function askToCloseExpenses(
    allocationsInput: ClientPaymentAllocationInput[],
  ): Promise<boolean> {
    const closable = allocationsInput
      .map((a) => payableExpenses.find((e) => e.id === a.expenseItemId))
      .filter((e): e is ExpenseItemWithRelations => Boolean(e) && e!.balance > 0.005);

    if (closable.length === 0) return false;

    const names = closable.map((e) => e.itemName ?? e.vendorName ?? e.vendorCategoryName);
    return confirm({
      title: 'Close reconciled expenses?',
      message: `Record this payment against ${
        closable.length === 1 ? 'the expense' : `the ${closable.length} expenses`
      } it was allocated to (${names.join(', ')})? A payment is added to each, and any that ends up fully paid moves to the paid-in-full status.`,
      confirmLabel: 'Yes, close them',
      cancelLabel: 'No, just record the payment',
      variant: 'info',
    });
  }

  async function handleSubmit() {
    setError(null);
    if (!paymentMethodId) {
      setError('Payment method is required.');
      return;
    }
    if (numericAmount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (allocationTotal > numericAmount + 0.005) {
      setError(
        `Allocations ($${allocationTotal.toFixed(2)}) exceed the payment amount ($${numericAmount.toFixed(2)}).`,
      );
      return;
    }

    const allocationsInput: ClientPaymentAllocationInput[] = Object.entries(allocations)
      .map(([expenseItemId, amt]) => ({ expenseItemId, amount: Number(amt) || 0 }))
      .filter((a) => a.amount > 0);

    const closeExpenses = await askToCloseExpenses(allocationsInput);

    try {
      if (isEdit && payment) {
        await update.mutateAsync({
          id: payment.id,
          updates: {
            amount: numericAmount,
            paymentMethodId,
            paidAt,
            reference: reference || null,
            notes: notes || null,
            allocations: allocationsInput,
            closeExpenses,
          },
        });
      } else {
        await create.mutateAsync({
          eventId,
          amount: numericAmount,
          paymentMethodId,
          paidAt,
          reference: reference || undefined,
          notes: notes || undefined,
          allocations: allocationsInput,
          closeExpenses,
        });
      }
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to save payment.');
    }
  }

  const remainingColor =
    remaining < -0.005 ? 'text-red-600' : remaining > 0.005 ? 'text-amber-600' : 'text-green-700';

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Client Payment' : 'Record Client Payment'}
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <span className="text-gray-500">Allocated</span>{' '}
            <span className="font-semibold">${allocationTotal.toFixed(2)}</span>
            {' / '}
            <span className="text-gray-500">Total</span>{' '}
            <span className="font-semibold">${numericAmount.toFixed(2)}</span>
            {' • '}
            <span className={remainingColor}>Remaining ${remaining.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={create.isPending || update.isPending}
              className="px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isEdit ? 'Save Changes' : 'Record Payment'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-gray-500">Amount</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
          <div className="block">
            <span className="text-xs text-gray-500">Payment Method</span>
            <div className="mt-1">
              <SearchableListSelect
                items={config?.paymentMethods ?? []}
                value={paymentMethodId || null}
                onChange={(v) => setPaymentMethodId(v as string)}
                getValue={(m) => m.id}
                getLabel={(m) => m.name}
                getSubtitle={(m) => (m.isProducerFunded ? 'Producer-funded' : null)}
                placeholder="Select…"
                ariaLabel="Payment Method"
              />
            </div>
          </div>
          <label className="block">
            <span className="text-xs text-gray-500">Paid At</span>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Reference / Check #</span>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-gray-500">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </label>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-900">Allocate to expenses</h3>
            <button
              type="button"
              onClick={autoAllocate}
              className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
            >
              Auto-allocate by due date
            </button>
          </div>

          {payableExpenses.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No producer-funded expenses on this event yet.
            </p>
          ) : (
            <div className="space-y-3">
              {groupedByVendor.map(([vendorName, items]) => (
                // Scrolls sideways on a phone rather than clipping the amount field.
                <div key={vendorName} className="border border-gray-200 rounded-lg overflow-x-auto">
                  <div className="px-3 py-2 bg-gray-50 text-xs font-medium text-gray-700">
                    {vendorName}
                  </div>
                  <table className="w-full min-w-[480px] text-sm">
                    <thead className="text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-1 text-left">Item</th>
                        <th className="px-3 py-1 text-right">Owed</th>
                        <th className="px-3 py-1 text-right">Already Paid</th>
                        <th className="px-3 py-1 text-right">Outstanding</th>
                        <th className="px-3 py-1 text-right">This Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((e) => {
                        const outstanding = e.clientBalance;
                        return (
                          <tr key={e.id} className="border-t border-gray-100">
                            <td className="px-3 py-1.5">
                              {e.itemName ?? e.vendorCategoryName}
                              {e.clientChargeType === 'income' && (
                                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700">
                                  income
                                </span>
                              )}
                              {e.clientChargeType === 'reimbursement' && (
                                <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700">
                                  reimbursement
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              ${e.price.toFixed(2)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                              ${e.clientPaid.toFixed(2)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                              ${outstanding.toFixed(2)}
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={allocations[e.id] ?? ''}
                                onChange={(ev) => setAllocationAmount(e.id, ev.target.value)}
                                placeholder="0.00"
                                className="w-24 px-2 py-1 border border-gray-200 rounded text-right text-sm"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
